import React, { useRef, useState, useCallback, useEffect } from 'react';
import styles from './file-input.module.css';

/**
 * FileInput component that looks like a button and displays image preview.
 *
 * @param {Object} props - Component props
 * @param {string=} props.label - Button label text
 * @param {string=} props.accept - File types to accept (default: "image/*")
 * @param {Function} props.onFileSelect - Callback when file is selected, receives base64 string
 * @param {string=} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {string} props.buttonVariant - Button variant (primary, secondary, tertiary)
 * @param {string} props.buttonColor - Button color (orange, black, white)
 * @param {string} props.buttonSize - Button size (small, medium, large)
 * @param {number=} props.maxFileSize - Maximum file size in bytes (default: 5MB)
 * @param {string} props.id - Input id
 * @param {File|null} props.selectedFile - Selected file to display (for restoring state)
 * @returns {React.ReactElement}
 */
export function FileInput({
  label = 'Choose File',
  accept = 'image' + '/' + '*',
  onFileSelect,
  className = '',
  disabled = false,
  buttonVariant = 'primary',
  buttonColor = 'orange',
  buttonSize = 'small',
  maxFileSize = 5 * 1024 * 1024,
  id,
  selectedFile = null
}) {
  const fileInputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const [previewUrl, setPreviewUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [file, setFile] = useState(/** @type {File | null} */ (selectedFile));

  // Restore preview when selectedFile is provided
  useEffect(() => {
    if (selectedFile && selectedFile instanceof File) {
      const preview = URL.createObjectURL(selectedFile);
      setPreviewUrl(preview);
      setFile(selectedFile);
    }
  }, [selectedFile]);

  // Clean up object URL when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  /**
   * Converts a file to base64 string.
   *
   * @param {{ file: File }} options - Options.
   * @param {File} options.file - The file to convert.
   * @returns {Promise<string>} Base64 string
   */
  const fileToBase64 = useCallback(({ file }) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }, []);

  /**
   * Validates if the selected file is an image.
   *
   * @param {Object} options - Options.
   * @param {File} options.file - The file to validate.
   * @param {string} options.accept - The accepted file extensions list (e.g. "png,jpg,jpeg,webp") or "*" or "image/*".
   * @param {number} options.maxFileSize - The maximum file size in bytes.
   * @returns {boolean} True if file is a valid image.
   */
  const validateImageFile = useCallback(({ file, accept, maxFileSize }) => {
    const normalizedAccept = typeof accept === 'string' ? accept.trim().toLowerCase() : '*';
    const acceptsAny = normalizedAccept === '*' || normalizedAccept === 'image' + '/' + '*';
    const acceptedExtensions = acceptsAny
      ? []
      : normalizedAccept
          .split(',')
          .map((type) => type.trim().toLowerCase())
          .filter(Boolean);

    const isImageMime = typeof file.type === 'string' ? file.type.toLowerCase().startsWith('image/') : false;
    if (!isImageMime) {
      setError('Please select a valid image file.');
      return false;
    }

    if (acceptedExtensions.length) {
      const fileType = file.type.toLowerCase();
      const fileName = typeof file.name === 'string' ? file.name.toLowerCase() : '';
      const fileExtension = fileName.includes('.') ? fileName.split('.').pop() : '';
      const matchesMime = acceptedExtensions.some((ext) => fileType === `image/${ext}`);
      const matchesExtension = fileExtension ? acceptedExtensions.includes(fileExtension) : false;

      if (!matchesMime && !matchesExtension) {
        setError(`Please select a valid image file (${acceptedExtensions.join(', ')}).`);
        return false;
      }
    }

    if (typeof maxFileSize === 'number' && file.size > maxFileSize) {
      setError(
        `File size must be less than ${maxFileSize / 1024 / 1024}MB. You uploaded a file of ${(file.size / 1024 / 1024).toFixed(2)}MB.`
      );
      return false;
    }

    return true;
  }, []);

  /**
   * Handles file selection from the input.
   *
   * @param {Event} event - File input change event
   */
  const handleFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      setFile(file);
      if (!file) {
        return;
      }

      // Reset error state
      setError('');
      setIsLoading(true);

      try {
        // Validate the file
        if (!validateImageFile({ file, accept, maxFileSize })) {
          return;
        }

        // Create preview URL
        const preview = URL.createObjectURL(file);
        setPreviewUrl(preview);

        // Convert to base64
        const base64 = await fileToBase64({ file });

        // Call the callback with base64 data
        if (onFileSelect) {
          onFileSelect({ userImage: { base64, file }, editedImage: { base64, file } });
        }
      } catch (err) {
        setError('Failed to process the image. Please try again.');
        console.error('File processing error:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [validateImageFile, fileToBase64, onFileSelect, accept, maxFileSize]
  );

  /**
   * Clears the selected file and preview.
   */
  const handleClear = useCallback(() => {
    setPreviewUrl('');
    setError('');
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onFileSelect) {
      onFileSelect({ userImage: null, editedImage: null });
    }
  }, [onFileSelect]);

  /**
   * Handles button click to trigger file input.
   */
  const handleButtonClick = useCallback(() => {
    handleClear();
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled, handleClear]);

  /**
   * Handles keyboard events for accessibility.
   *
   * @param {KeyboardEvent} event - Keyboard event
   */
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleButtonClick();
      }
    },
    [handleButtonClick]
  );

  // Build button classes based on variant and color
  const buttonClasses = [
    'button',
    `button--${buttonVariant}`,
    `button--${buttonColor}`,
    `button--${buttonSize}`,
    styles['file-input__button'],
    className
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles['file-input']}>
      {/* Image preview */}
      {previewUrl && file && (
        <div className={styles['file-input__preview']}>
          <div className={styles['file-input__preview-container']}>
            <div className={styles['file-input__preview-icon']}>
              <img src={previewUrl} alt="File preview" className={styles['file-input__preview-image']} />
            </div>
            <div className={styles['file-input__preview-content']}>
              <span className={styles['file-input__preview-filename'] + ' body-sm'}>{file.name}</span>
            </div>
            <button
              type="button"
              className={styles['file-input__preview-remove'] + ' button-reset'}
              onClick={handleClear}
              aria-label="Remove image"
            >
              <span className={'body-xs ' + styles['file-input__preview-remove-text']}>Remove</span>
              <span className={styles['file-input__preview-remove-icon-wrapper'] + ' svg-wrapper svg-wrapper--small'}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M5.95001 5.3839C5.79381 5.22768 5.54054 5.22766 5.38432 5.38387C5.22811 5.54007 5.22809 5.79333 5.38429 5.94955L7.43483 8.00031L5.38479 10.0506C5.22859 10.2068 5.2286 10.46 5.38482 10.6162C5.54104 10.7724 5.79431 10.7724 5.95051 10.6162L8.00049 8.56603L10.0505 10.6162C10.2067 10.7724 10.4599 10.7724 10.6161 10.6162C10.7724 10.46 10.7724 10.2068 10.6162 10.0506L8.56614 8.00032L10.6167 5.94955C10.7729 5.79333 10.7729 5.54007 10.6167 5.38387C10.4605 5.22766 10.2072 5.22768 10.051 5.3839L8.00049 7.4346L5.95001 5.3839Z"
                    fill="currentColor"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M8.00002 0.93335C4.0972 0.93335 0.93335 4.0972 0.93335 8.00002C0.93335 11.9028 4.0972 15.0667 8.00002 15.0667C11.9028 15.0667 15.0667 11.9028 15.0667 8.00002C15.0667 4.0972 11.9028 0.93335 8.00002 0.93335ZM1.73335 8.00002C1.73335 4.53903 4.53903 1.73335 8.00002 1.73335C11.461 1.73335 14.2667 4.53903 14.2667 8.00002C14.2667 11.461 11.461 14.2667 8.00002 14.2667C4.53903 14.2667 1.73335 11.461 1.73335 8.00002Z"
                    fill="currentColor"
                  />
                </svg>
              </span>
            </button>
          </div>
        </div>
      )}
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className={styles['file-input__input']}
        disabled={disabled}
        aria-label="File input"
        id={id}
      />

      {/* Button that triggers file input */}
      <button
        type="button"
        className={buttonClasses}
        onClick={handleButtonClick}
        onKeyDown={handleKeyDown}
        disabled={disabled || isLoading}
        aria-label={label}
      >
        {isLoading ? (
          <span className="loading__spinner">
            <svg className="spinner" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle
                className="path"
                cx="10"
                cy="10"
                r="8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="50"
                strokeDashoffset="20"
              />
            </svg>
          </span>
        ) : (
          <>{label}</>
        )}
      </button>
      {!file && <p className={styles['file-input__no-file-chosen'] + ' body-sm'}>No files chosen</p>}
      {/* Error message */}
      {error && (
        <div className={styles['file-input__error'] + ' body-sm font-medium'} role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
